import { useCallback } from 'react';

import {
	createWithOverrides,
	RecommendationError,
	useExecuteMSRNudge,
} from '@atlassian/cross-join-nudges';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';

import { useFireOnlyNewEvent } from '../hooks/use-fire-only-new-event';

import { cjnEligibilityCacheHandler, isCacheEnabled, withEligibilityCache } from './caching';
import { hasSeenAnyNudgeExceptMsr } from './has-seen-nudge-check';
import { useOverrides } from './overrides';
import type { CrossJoinNudgesInput, EligibilityCheck, MsrOverrideKeys } from './msr/msr-types';
import {
	fireCustomExperimentError,
	fireMsrCjnEligibilityBlocked,
	getCustomExperimentAnalytics,
} from './analytics';

export const getHasBeenEnrolledInMsr = () => {
	const cachedHasEnrolledMsr = withEligibilityCache('has_enrolled_msr_v1', async () => {
		// The first time this is accessed without being set, it will be set false
		return false;
	});
	return cachedHasEnrolledMsr();
};

export const setEnrolledInMsr = () => {
	return cjnEligibilityCacheHandler.set('has_enrolled_msr_v1', true);
};

export const checkMsrCrossJoinNudgeEligibility = async () => {
	// MSR relies too heavily on localstorage to allow any users who have it disabled
	if (!(await isCacheEnabled())) {
		return false;
	}

	// Existing users of MSR variant/control
	if (await getHasBeenEnrolledInMsr()) {
		return true;
	}

	// Users who should be put into MSR variant/control
	if (!hasSeenAnyNudgeExceptMsr()) {
		return true;
	}

	return false;
};

export function getMsrFeatureVisited(): boolean {
	return localStorage.getItemAsBoolean(localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_KEY) || false;
}

export function setMsrFeatureVisited(daysExpiry: number): void {
	return localStorage.setItem(
		localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_KEY,
		true,
		daysExpiry * 86_400,
	);
}

export function getMsrFeatureVisitedCount(): number {
	const val = Number(localStorage.getItem(localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_COUNT));
	if (Number.isNaN(val)) return 0;
	return val;
}

export function setMsrFeatureVisitedCount(count: number): void {
	return localStorage.setItem(localStorageKeys.CROSS_JOIN_NUDGES_MSR_VISITED_COUNT, count);
}

/**
 * Eligibility can be bypassed using the following query parameter
  ?cjn_msr_overrides=
 	 feature_visited_too_recently,
	 user_already_has_jira,
	 user_already_has_jpd,
	 jira_is_too_new,
	 jpd_is_too_new,
	 site_is_opted_out,
	 user_cannot_request_jira_access,
	 user_cannot_request_jpd_access
  }
 */
export const useRunMsrNudges = ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
}: CrossJoinNudgesInput) => {
	const overrides = useOverrides<MsrOverrideKeys>('cjn_msr_overrides');
	const withOverrides = createWithOverrides(overrides);
	const customAnalytics = getCustomExperimentAnalytics('crossJoinNudges', createAnalyticsEvent);
	const fireOnlyNewEvent = useFireOnlyNewEvent();

	const executeNudge = useExecuteMSRNudge({
		cloudId,
		getFeatureVisited: getMsrFeatureVisited,
		getFeatureVisitedCount: getMsrFeatureVisitedCount,
		onEligibilityReturned: ({
			eligibilityResult,
			key,
		}: {
			eligibilityResult: EligibilityCheck;
			key: string;
		}) => {
			fireOnlyNewEvent({
				value: eligibilityResult,
				key,
			});
		},
		onNoMsrResultsReturned: () => {
			fireMsrCjnEligibilityBlocked(customAnalytics, 'no recommendations');
		},
		onError: (err: unknown) => {
			if (err instanceof RecommendationError) {
				fireCustomExperimentError(
					customAnalytics,
					err.options.cause,
					'msrCjnFetchRecommendation',
					'failed',
					{ cloudId },
				);
			} else {
				fireCustomExperimentError(customAnalytics, err, 'executeMSRNudge', 'failed', { cloudId });
			}
			fireMsrCjnEligibilityBlocked(customAnalytics, 'unexpected error');
		},
		overrides,
		product: 'confluence',
		subProduct: '',
		setFeatureVisited: setMsrFeatureVisited,
		setFeatureVisitedCount: setMsrFeatureVisitedCount,
	});

	return useCallback(async () => {
		const { getNudges } = await import(
			/* webpackChunkName: "loadable-msr-cross-join-nudges-nudges" */ './msr/msr-nudges'
		);
		const nudges = getNudges({ cloudId, userId, createAnalyticsEvent, flags, push, withOverrides });

		return executeNudge({ nudges });
	}, [cloudId, userId, createAnalyticsEvent, flags, push, executeNudge, withOverrides]);
};
